import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Customer } from "../../../../public/models/customersModels";
import { PublicServiceRequestCustomer } from "../../../serviceBoard/models/models";

interface RfsCompanyCardProps {
  embeddedCustomer: PublicServiceRequestCustomer;
  customer: Customer | null;
  isDirectToMe?: boolean;
}

const RfsCompanyCard = ({ embeddedCustomer, customer, isDirectToMe }: RfsCompanyCardProps) => {
  const { translate } = useTranslation(["serviceBoard", "servicing"]);
  const isPhone = useMediaQuery("(max-width:768px)");

  const logoSize = isPhone ? 32 : 48;
  const logoUrl = embeddedCustomer.logoUrl;
  const companyName = embeddedCustomer.name;
  const companyId = embeddedCustomer.id;

  const city = customer?.address?.city;
  const countryName = customer?.address?.countryName;
  const phone = customer?.phone;
  const email = customer?.email;
  const website = customer?.website;

  const locationText = [city, countryName].filter(Boolean).join(", ");

  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: "14px",
        padding: isPhone ? "12px 14px" : "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: isPhone ? 1 : 1.5,
      }}
    >
      {/* Company name + logo */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={companyName}
            sx={{
              width: logoSize,
              height: logoSize,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
              border: `2px solid ${dt.cardBorder}`,
            }}
          />
        ) : (
          <Box
            sx={{
              width: logoSize,
              height: logoSize,
              borderRadius: "50%",
              backgroundColor: dt.blueSoft,
              border: `2px solid ${dt.blueGlow}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Box sx={{ fontFamily: dt.font, fontSize: isPhone ? "13px" : "16px", fontWeight: 700, color: dt.blue }}>
              {companyName?.charAt(0)?.toUpperCase() || "?"}
            </Box>
          </Box>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            component={RouterLink}
            to={`/companies/${companyId}`}
            sx={{
              fontFamily: dt.font,
              fontSize: isPhone ? "14px" : "16px",
              fontWeight: 700,
              color: dt.textLink,
              textDecoration: "none",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {companyName}
          </Box>
        </Box>
      </Box>

      {/* Partnership badge */}
      {isDirectToMe && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            alignSelf: "flex-start",
            gap: "6px",
            backgroundColor: "#E8F8F5",
            color: "#27AE60",
            border: "1px solid #A3E4D7",
            borderRadius: "20px",
            padding: "3px 12px",
            fontSize: "11px",
            fontWeight: 600,
            fontFamily: dt.font,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: "13px" }} />
          {translate("servicing:rfsDetail.directRequest")}
        </Box>
      )}

      {/* Location */}
      {locationText && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <LocationOnOutlinedIcon sx={{ fontSize: 18, color: dt.textSecondary }} />
          <Box
            sx={{
              fontSize: "13px",
              fontFamily: dt.font,
              fontWeight: 500,
              color: dt.textSecondary,
            }}
          >
            {locationText}
          </Box>
        </Box>
      )}

      {/* Contact info */}
      {(phone || email || website) && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {phone && (
            <Box
              component="a"
              href={`tel:${phone}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                textDecoration: "none",
                color: dt.textSecondary,
                "&:hover": { color: dt.textLink },
                transition: "color 0.15s",
              }}
            >
              <PhoneOutlinedIcon sx={{ fontSize: 18 }} />
              <Box sx={{ fontSize: "13px", fontFamily: dt.font, fontWeight: 500 }}>{phone}</Box>
            </Box>
          )}
          {email && (
            <Box
              component="a"
              href={`mailto:${email}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                textDecoration: "none",
                color: dt.textSecondary,
                "&:hover": { color: dt.textLink },
                transition: "color 0.15s",
                overflow: "hidden",
              }}
            >
              <EmailOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
              <Box
                sx={{
                  fontSize: "13px",
                  fontFamily: dt.font,
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {email}
              </Box>
            </Box>
          )}
          {website && (
            <Box
              component="a"
              href={website.startsWith("http") ? website : `https://${website}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                textDecoration: "none",
                color: dt.textSecondary,
                "&:hover": { color: dt.textLink },
                transition: "color 0.15s",
                overflow: "hidden",
              }}
            >
              <LanguageOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
              <Box
                sx={{
                  fontSize: "13px",
                  fontFamily: dt.font,
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {website.replace(/^https?:\/\/(www\.)?/, "")}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default RfsCompanyCard;
